import InfoHelp from "../../components/help/InfoHelp";
import { metricHelp } from "../../components/help/helpText";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  FileSearch2,
  RefreshCcw,
  Target,
  Users,
} from "lucide-react";

const typeLabel = {
  action: "Ação",
  evidence: "Evidência",
  document_review: "Documento",
  task: "Pendência",
  obligation: "Obrigação",
  visit: "Visita",
  fire: "Incêndio",
  automation: "Automação",
  follow_up: "Contato",
  training: "Treinamento",
};

function Metric({ icon: Icon, label, value, hint, danger = false }) {
  return (
    <Card className="min-w-0 border-border/75">
      <CardContent className="min-w-0 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium leading-snug text-muted-foreground">
              {label}<InfoHelp label={label}>{metricHelp[label] || hint || "Indicador calculado para os registros e filtros desta tela."}</InfoHelp>
            </div>
            <div
              className={`mt-2 font-mono text-2xl font-semibold leading-none tabular-nums ${danger ? "text-destructive" : "text-foreground"}`}
            >
              {value}
            </div>
            {hint && (
              <div className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {hint}
              </div>
            )}
          </div>
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Icon className="size-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PriorityRow({ item, numbered }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border/70 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {numbered && (
            <span className="grid size-6 place-items-center rounded-full bg-primary/10 font-mono text-[11px] font-semibold text-primary">
              {numbered}
            </span>
          )}
          <Badge
            variant={
              item.score >= 90
                ? "destructive"
                : item.score >= 75
                  ? "secondary"
                  : "outline"
            }
          >
            {typeLabel[item.type] || item.type}
          </Badge>
          {item.priority === "critica" && (
            <Badge variant="destructive">Urgente</Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {item.client_name || "Interno"}
          </span>
        </div>
        <div className="mt-2 text-sm font-medium leading-snug">
          {item.title}
        </div>
        {(item.subtitle || item.reason) && (
          <div className="mt-1 text-xs text-muted-foreground">
            {[item.reason, item.subtitle].filter(Boolean).join(" · ")}
          </div>
        )}
        {item.responsavel && (
          <div className="mt-1 text-xs text-muted-foreground">
            Responsável: {item.responsavel}
          </div>
        )}
        {item.due_at && (
          <div className="mt-2 text-xs text-muted-foreground">
            Prazo: {String(item.due_at).slice(0, 10)}
          </div>
        )}
      </div>
      {item.href && (
        <Link to={item.href}>
          <Button size="sm" variant="outline">
            Abrir <ArrowRight className="size-3.5" />
          </Button>
        </Link>
      )}
    </div>
  );
}

function DetailSection({ title, items, empty }) {
  return (
    <div>
      <div className="mb-3 text-sm font-semibold">{title}</div>
      <div className="space-y-2">
        {(items || []).slice(0, 10).map((item) => (
          <PriorityRow key={`${item.type}-${item.id}`} item={item} />
        ))}
        {!(items || []).length && (
          <div className="rounded-xl border border-dashed p-5 text-center text-sm text-muted-foreground">
            {empty}
          </div>
        )}
      </div>
    </div>
  );
}

function CommercialRadar({ lifecycle, onContact }) {
  const items = (lifecycle?.opportunities || [])
    .filter((item) => item.status === "pendente")
    .slice(0, 6);
  if (!items.length) return null;
  return (
    <Card>
      <CardContent className="p-5 sm:p-6">
        <div className="mb-4 flex items-start gap-3">
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Target className="size-4" />
          </div>
          <div>
            <div className="font-semibold">Clientes para contatar</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Renovações e oportunidades que já merecem retorno.
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.cycle_key}
              className="flex flex-col gap-3 rounded-xl border border-border/70 p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant={
                      item.type === "renovacao" ? "default" : "secondary"
                    }
                  >
                    {item.type === "renovacao" ? "Renovação" : "Novo contato"}
                  </Badge>
                  <span className="truncate text-sm font-medium">
                    {item.client_name}
                  </span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Proposta {item.proposal_code || "—"} · referência{" "}
                  {String(item.anniversary_date || "").slice(0, 10)}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {item.proposal_id && (
                  <Link to={`/propostas/${item.proposal_id}`}>
                    <Button size="sm" variant="outline">
                      Abrir proposta
                    </Button>
                  </Link>
                )}
                <Button size="sm" onClick={() => onContact(item)}>
                  Marcar contato
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function TodayCEO() {
  const { isAdmin } = useAuth();
  const [quality, setQuality] = useState(null);
  const [data, setData] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [todayResult, lifecycleResult, qualityResult] =
        await Promise.allSettled([
          api.get("/efficiency/today"),
          api.get("/commercial-lifecycle"),
          isAdmin ? api.get("/efficiency/data-quality") : Promise.resolve(null),
        ]);
      if (todayResult.status === "fulfilled") setData(todayResult.value.data);
      else
        toast.error(
          todayResult.reason?.response?.data?.detail ||
            "Falha ao carregar as pendências",
        );
      if (lifecycleResult.status === "fulfilled")
        setLifecycle(lifecycleResult.value.data);
      if (qualityResult.status === "fulfilled" && qualityResult.value)
        setQuality(qualityResult.value.data);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const markContacted = async (item) => {
    try {
      await api.patch(
        `/commercial-lifecycle/${encodeURIComponent(item.cycle_key)}/status`,
        {
          status: "contatado",
          observacao: "Contato registrado na tela de prioridades",
        },
      );
      toast.success("Contato registrado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao registrar contato");
    }
  };

  const calendar = useMemo(() => data?.calendar_today || [], [data]);
  const annualPending = lifecycle?.counts?.pendente || 0;
  const commercialTotal = (data?.counts?.commercial || 0) + annualPending;

  return (
    <>
      <Header
        title="Minha rotina"
        subtitle="Comece pelo que realmente exige ação hoje."
        actions={
          <Button variant="outline" onClick={load}>
            <RefreshCcw className="size-4" /> Atualizar
          </Button>
        }
      />
      <div className="page-container space-y-6" data-testid="today-ceo-page">
        {loading && !data && (
          <div className="h-36 animate-pulse rounded-2xl border bg-card/50" />
        )}
        {!loading && data && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Metric
                icon={AlertTriangle}
                label="Atrasados ou para hoje"
                value={data.counts?.today || 0}
                hint="Itens que precisam de ação imediata"
                danger={(data.counts?.critical || 0) > 0}
              />
              <Metric
                icon={FileSearch2}
                label="Para conferir"
                value={data.counts?.review || 0}
                hint="Evidências e documentos aguardando revisão"
              />
              <Metric
                icon={Clock3}
                label="Vencem em breve"
                value={data.counts?.week || 0}
                hint="Itens que já vale a pena preparar"
              />
              <Metric
                icon={Users}
                label="Clientes para contatar"
                value={commercialTotal}
                hint="Follow-ups, renovações e reativações"
              />
            </div>

            <Card className="border-primary/20">
              <CardContent className="p-5 sm:p-6">
                <div className="mb-4">
                  <div className="text-xs font-medium text-muted-foreground">
                    Por onde começar
                  </div>
                  <h2 className="mt-1 text-lg font-semibold">
                    Próximas ações recomendadas
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Esta lista concentra as prioridades para evitar repetir os
                    mesmos alertas em vários blocos.
                  </p>
                </div>
                <div className="space-y-2">
                  {(data.recommended_next || []).map((item, index) => (
                    <PriorityRow
                      key={`${item.type}-${item.id}`}
                      item={item}
                      numbered={index + 1}
                    />
                  ))}
                  {!data.recommended_next?.length && (
                    <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
                      <CheckCircle2 className="mx-auto mb-2 size-5 text-primary" />
                      Não há nenhuma prioridade urgente no momento.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-6 xl:grid-cols-[1fr_.85fr]">
              <CommercialRadar
                lifecycle={lifecycle}
                onContact={markContacted}
              />
              <Card>
                <CardContent className="p-5 sm:p-6">
                  <div className="mb-4">
                    <div className="text-xs font-medium text-muted-foreground">
                      Agenda
                    </div>
                    <h2 className="mt-1 text-lg font-semibold">
                      Compromissos de hoje
                    </h2>
                  </div>
                  <div className="space-y-2">
                    {calendar.map((event) => (
                      <div
                        key={`${event.source}-${event.id}`}
                        className="rounded-xl border border-border/70 p-3"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant={
                              event.source === "outlook"
                                ? "secondary"
                                : "outline"
                            }
                          >
                            {event.source === "outlook" ? "Outlook" : "CEO"}
                          </Badge>
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {String(event.start || "").slice(11, 16) ||
                              "dia todo"}
                          </span>
                        </div>
                        <div className="mt-2 text-sm font-medium">
                          {event.title}
                        </div>
                        {event.client_name && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {event.client_name}
                          </div>
                        )}
                        {event.location && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {event.location}
                          </div>
                        )}
                        <div className="mt-2 flex gap-2">
                          {event.href && (
                            <Link to={event.href}>
                              <Button size="sm" variant="outline">
                                Abrir visita
                              </Button>
                            </Link>
                          )}
                          {event.web_link && (
                            <a
                              href={event.web_link}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Button size="sm" variant="outline">
                                Abrir Outlook
                              </Button>
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                    {!calendar.length && (
                      <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                        Nenhum compromisso registrado para hoje.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {quality && (
              <details className="rounded-2xl border border-border bg-card p-5">
                <summary className="cursor-pointer font-semibold">
                  Qualidade dos cadastros · {quality.findings_count} ponto(s) a
                  conferir
                </summary>
                <p className="mt-2 text-sm text-muted-foreground">
                  {quality.clients_checked} cliente(s) verificado(s).
                  {quality.truncated
                    ? " Auditoria limitada aos primeiros 5.000 cadastros."
                    : ""}{" "}
                  Confira os vínculos antes de consolidar cadastros repetidos.
                </p>
                <div className="mt-3 space-y-2">
                  {quality.findings.slice(0, 10).map((row, i) => (
                    <div
                      key={`${row.client_id}-${row.kind}-${i}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
                    >
                      <div>
                        <div className="text-sm font-medium">
                          {row.client_name}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {row.message}
                        </p>
                      </div>
                      <Link to={row.href}>
                        <Button variant="outline" size="sm">
                          Conferir cadastro
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <details className="rounded-2xl border border-border bg-card p-5 sm:p-6">
              <summary className="cursor-pointer font-semibold">
                Ver todas as pendências por categoria
              </summary>
              <p className="mt-1 text-sm text-muted-foreground">
                Use esta área quando precisar investigar além das prioridades
                mostradas acima.
              </p>
              <div className="mt-5 grid gap-6 xl:grid-cols-2">
                <DetailSection
                  title="Atrasados e para hoje"
                  items={data.sections?.today}
                  empty="Nada vencido ou marcado para hoje."
                />
                <DetailSection
                  title="Aguardando conferência"
                  items={data.sections?.review}
                  empty="Nada aguardando conferência."
                />
                <DetailSection
                  title="Próximos vencimentos"
                  items={data.sections?.week}
                  empty="Nenhum vencimento próximo."
                />
                <DetailSection
                  title="Contatos comerciais"
                  items={data.sections?.commercial}
                  empty="Nenhum contato comercial pendente."
                />
              </div>
            </details>
          </>
        )}
      </div>
    </>
  );
}
